import React, { useEffect, useState, useRef, forwardRef } from "react";
import cx from "classnames";

import { useKeyboard } from "@/v2/shared/hooks/useKeyboard";
import styles from "./styles.module.scss";

export type InputType = "none" | "instruction" | "feedback";

export const SimulatorInput = forwardRef<
  HTMLTextAreaElement,
  {
    value: string;
    type: InputType;
    setType: (v: InputType) => void;
    handleInputChange: (e: any) => void;
    messagesLength: number;
    handleSubmit: () => void;
    handleResetConversation: () => void;
    disableInstructions?: boolean;
  }
>(
  (
    {
      value,
      type,
      setType,
      handleInputChange,
      messagesLength,
      handleSubmit,
      handleResetConversation,
      disableInstructions = false,
    },
    textareaRef
  ) => {
    const [, setValue] = useState("");
    const hasInteractedRef = useRef(false);

    useEffect(() => {
      if (hasInteractedRef.current) {
      (textareaRef as React.RefObject<HTMLTextAreaElement>).current?.focus();
      }
    }, [type]);

    useEffect(() => {
      const handleUserInteraction = () => {
        hasInteractedRef.current = true;
      };
      
      window.addEventListener('click', handleUserInteraction, { once: true });
      window.addEventListener('keydown', handleUserInteraction, { once: true });
      
      return () => {
        window.removeEventListener('click', handleUserInteraction);
        window.removeEventListener('keydown', handleUserInteraction);
      };
    }, []);

    useKeyboard(() => {
      setType("none");
      setValue("");
      if ((textareaRef as React.RefObject<HTMLTextAreaElement>).current) {
        (textareaRef as any).current.style.height = "24px";
      }
    }, [27]);

    useKeyboard(
      (e) => {
        if (e.target instanceof HTMLTextAreaElement) {
          return;
        }
        if (!e.shiftKey && !disableInstructions) {
          setTimeout(() => {
            setType("instruction");
            (
              textareaRef as React.RefObject<HTMLTextAreaElement>
            ).current?.focus();
          }, 300);
        }
      },
      [191],
      {
        stopPropagation: false,
        preventDefault: false,
        disableGlobalEvent: false,
      }
    );

    useKeyboard(
      (e) => {
        if (e.metaKey || e.ctrlKey) {
          handleResetConversation();
        }
      },
      [73],
      {
        event: "keydown",
        stopPropagation: false,
        preventDefault: false,
        disableGlobalEvent: false,
      }
    );

    return (
      <div className={cx(styles.simulatorInputWrapper)}>
        <textarea
          ref={textareaRef}
          value={value}
          dir="auto"
          onChange={(e) => {
            handleInputChange(e);
            setValue(e.target.value);
          }}
          rows={1}
          placeholder={
            type === "none"
              ? messagesLength === 0
                ? "Ask anything else or add general instruction "
                : "Ask a follow up"
              : type === "instruction"
              ? "Add an instruction"
              : "Why its not a good response ?"
          }
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSubmit();
              setTimeout(() => {
                (e.target as any).style.height = "auto";
              }, 30);
            }
            if (e.keyCode === 191 && !e.shiftKey) {
              e.preventDefault();
            }
          }}
          onInput={(e) => {
            (e.target as any).style.height = "auto";
            (e.target as any).style.height = `${
              (e.target as any).scrollHeight
            }px`;
          }}
        />
      </div>
    );
  }
);
